import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Tag,
  Layout,
  Row,
  Typography,
  Modal,
  message,
  Switch,
  Popconfirm,
  Select,
  Segmented,
  Checkbox,
} from "antd";
import type { InputRef } from "antd";
import moment from "moment";
import {
  DeleteOutlined,
  SearchOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import debounce from "lodash.debounce";
import axios from "../../services/axiosInstance";
import type { ColumnType, TableProps } from "antd/es/table";
import {
  DELETE_DOCUMENT,
  FETCH_ALL_DOCUMENTS,
  FETCH_SIGNED_DOCUMENTS,
  TOGGLE_DOCUMENT_AS_APPLIED,
  TOGGLE_REMINDER_EMAIL_AS_RENEWED,
} from "../../services/apiEndPoints";
import { IDatabase } from "../types/document.types";
import { imageInfo, snakeToCapitalCase } from "../upload/DualUpload";

const { Title, Link } = Typography;

export const isPdf = (url: string) => url.toLowerCase().includes(".pdf");

const DocumentTableComponent = () => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [globalSearch, setGlobalSearch] = useState("");
  const [data, setData] = useState<IDatabase[]>([]);
  const searchInput = useRef<InputRef>(null);

  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const [signedUrl, setSignedUrl] = useState<string | null>(null);
  const [loadingPreview, setLoadingPreview] = useState(false);

  const [selectedDocTypes, setSelectedDocTypes] = useState<string[]>([]);
  const [selectedLocations, setSelectedLocations] = useState<string[]>([]);
  const [tableSize, setTableSize] = useState<TableProps["size"]>("middle");

  /* ---------------- API ---------------- */

  const fetchSignedUrl = async (s3Url: string) => {
    const response = await axios.get(
      FETCH_SIGNED_DOCUMENTS + `?s3Url=${encodeURIComponent(s3Url)}`
    );
    if (response?.data?.results) return response.data.results;
    throw new Error("Failed to fetch signed URL");
  };

  const fetchDocuments = async () => {
    const response = await axios.get(FETCH_ALL_DOCUMENTS);
    if (response?.data?.results) setData(response.data.results);
  };

  useEffect(() => {
    fetchDocuments();
  }, []);

  const downloadVisibleTableAsCSV = () => {
    if (!filteredData.length) {
      message.warning("No data to export");
      return;
    }

    const headers = [
      "S.No",
      "Document No",
      "Document Type",
      "Expiry Date",
      "Company Name",
      "Location",
      "Details",
      "Uploaded By",
      "Renewed",
      "Applied",
    ];

    const rows = filteredData.map((item) => {
      const meta =
        item.meta && typeof item.meta === "object"
          ? Object.entries(item.meta)
              .map(([k, v]) => `${snakeToCapitalCase(k)}: ${v}`)
              .join(" | ")
          : "";

      return [
        item.sNo || "",
        item.uniqId || "",
        snakeToCapitalCase(item.documentType || ""),
        item.expiryDate ? moment(item.expiryDate).format("YYYY-MM-DD") : "",
        item.companyName || "",
        item.location || "",
        meta,
        item.uploadedBy || "",
        item.isRenewed ? "Yes" : "No",
      ];
    });

    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(",")
      ),
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);

    const link = document.createElement("a");
    link.href = url;
    link.download = `documents_${moment().format("YYYY_MM_DD_HH_mm")}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(url);

    message.success("CSV downloaded");
  };

  /* ---------------- Actions ---------------- */

  const handleRenewToggle = async (record: IDatabase) => {
    try {
      if (!record.isRenewed) {
        await axios.put(
          TOGGLE_REMINDER_EMAIL_AS_RENEWED(record.documentId),
          {}
        );
        message.success("Renewal reminder set");
        fetchDocuments();
      }
    } catch {
      message.error("Failed to update renewal status");
    }
  };

  const handleDelete = async (record: IDatabase) => {
    try {
      await axios.delete(DELETE_DOCUMENT(record.documentId));
      message.success("Document deleted");
      fetchDocuments();
    } catch {
      message.error("Failed to delete document");
    }
  };

  /* ---------------- Column Search ---------------- */

  const handleSearch = useMemo(
    () =>
      debounce((selectedKeys, confirm, dataIndex) => {
        confirm();
        setSearchText(selectedKeys[0]);
        setSearchedColumn(dataIndex);
      }, 250),
    []
  );

  const getColumnSearchProps = (
    dataIndex: keyof IDatabase
  ): ColumnType<IDatabase> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8, width: 240 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${String(dataIndex)}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8 }}
        />
        <Space>
          <Button
            type="primary"
            size="small"
            icon={<SearchOutlined />}
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
          >
            Search
          </Button>
          <Button size="small" onClick={() => clearFilters?.()}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ?.toString()
        .toLowerCase()
        .includes(String(value).toLowerCase()) || false,
    render: (text: string) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text || ""}
        />
      ) : (
        <span style={{ whiteSpace: "nowrap" }}>{text}</span>
      ),
  });

  const handleAppliedToggle = async (record: IDatabase, checked: boolean) => {
    setData((prev) =>
      prev.map((item) =>
        item.documentId === record.documentId
          ? { ...item, isApplied: checked }
          : item
      )
    );
    const setApplied = await axios.put(
      TOGGLE_DOCUMENT_AS_APPLIED(record.documentId),
      {}
    );
    message.success("Document Applied");
    fetchDocuments();
  };

  /* ---------------- Select Options ---------------- */

  const documentTypeOptions = useMemo(() => {
    const unique = Array.from(
      new Set(data.map((d) => d.documentType).filter(Boolean))
    );
    return unique.map((type) => ({
      label: snakeToCapitalCase(type as string),
      value: type as string,
    }));
  }, [data]);

  const locationOptions = useMemo(() => {
    const unique = Array.from(
      new Set(data.map((d) => d.location).filter(Boolean))
    );
    return unique.map((loc) => ({
      label: loc as string,
      value: loc as string,
    }));
  }, [data]);

  const filteredData = useMemo(() => {
    return data.filter((item) => {
      const typeMatch =
        selectedDocTypes.length === 0 ||
        selectedDocTypes.includes(item.documentType as string);

      const locationMatch =
        selectedLocations.length === 0 ||
        selectedLocations.includes(item.location as string);

      const globalMatch =
        !globalSearch ||
        Object.values(item)
          .join(" ")
          .toLowerCase()
          .includes(globalSearch.toLowerCase());

      return typeMatch && locationMatch && globalMatch;
    });
  }, [data, selectedDocTypes, selectedLocations, globalSearch]);

  /* ---------------- Columns ---------------- */

  const columns = useMemo<ColumnType<IDatabase>[]>(
    () => [
      {
        title: "S.No",
        key: "sNo",
        dataIndex: "sNo",
        width: 70,
        align: "center",
        render: (value) => value,
      },
      {
        title: "Document No",
        dataIndex: "uniqId",
        key: "uniqId",
        width: 160,
        render: (value?: string) => value || "-",
      },
      {
        title: "Type",
        dataIndex: "documentType",
        key: "documentType",
        sorter: (a, b) =>
          (a.documentType || "").localeCompare(b.documentType || ""),
        ...getColumnSearchProps("documentType"),
      },
      {
        title: "Expiry Date",
        dataIndex: "expiryDate",
        sorter: (a, b) =>
          moment(a.expiryDate).unix() - moment(b.expiryDate).unix(),
        render: (date) => (date ? moment(date).format("MMM D, YYYY") : "-"),
      },
      {
        title: "Company Name",
        dataIndex: "companyName",
        key: "companyName",
        sorter: (a, b) =>
          (a.companyName || "").localeCompare(b.companyName || ""),
        ...getColumnSearchProps("companyName"),
        render: (value?: string) => value || "-",
      },
      {
        title: "Location",
        dataIndex: "location",
        key: "location",
        sorter: (a, b) => (a.location || "").localeCompare(b.location || ""),
        ...getColumnSearchProps("location"),
        render: (location?: string) => location || "-",
      },
      {
        title: "Details",
        dataIndex: "meta",
        width: 420,
        render: (meta) => {
          if (!meta) return "-";
          if (typeof meta === "object" && !Array.isArray(meta)) {
            return imageInfo(meta).map(({ key, value }) => (
              <Tag
                key={key}
                color="blue"
                style={{ marginBottom: 2, marginRight: 2, fontSize: 10 }}
              >
                <b>{snakeToCapitalCase(key)}</b>: {String(value)}
              </Tag>
            ));
          }
          if (Array.isArray(meta)) {
            return meta.map((item) => (
              <Tag key={item} color="blue">
                {item}
              </Tag>
            ));
          }
          return "-";
        },
      },
      {
        title: "By",
        dataIndex: "uploadedBy",
      },
      {
        title: "URL",
        dataIndex: "s3Url",
        render: (s3Url: string) =>
          s3Url ? (
            <Link
              onClick={async () => {
                setLoadingPreview(true);
                try {
                  const url = await fetchSignedUrl(s3Url);
                  setSignedUrl(url);
                  setIsPreviewOpen(true);
                } catch {
                  message.error("Failed to load document");
                } finally {
                  setLoadingPreview(false);
                }
              }}
            >
              View
            </Link>
          ) : (
            "-"
          ),
      },
      {
        title: "Renewed",
        dataIndex: "isRenewed",
        render: (_, record) => (
          <Switch
            checked={record.isRenewed}
            onChange={() => handleRenewToggle(record)}
          />
        ),
      },
      {
        title: "Applied",
        dataIndex: "isApplied",
        align: "center",
        render: (_, record) => (
          <Checkbox
            checked={!!record.isApplied}
            onChange={(e) => handleAppliedToggle(record, e.target.checked)}
          />
        ),
      },

      {
        title: "Action",
        align: "center",
        render: (_, record) => (
          <Popconfirm
            title="Delete document?"
            onConfirm={() => handleDelete(record)}
          >
            <DeleteOutlined style={{ color: "#ff4d4f", cursor: "pointer" }} />
          </Popconfirm>
        ),
      },
    ],
    [searchText, searchedColumn]
  );

  /* ---------------- UI ---------------- */

  return (
    <Layout style={{ padding: 16 }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 12 }}>
        <Title level={3}>Document Directory</Title>

        <Space wrap>
          <Input
            allowClear
            prefix={<SearchOutlined />}
            placeholder="Global search"
            style={{ width: 220 }}
            value={globalSearch}
            onChange={(e) => setGlobalSearch(e.target.value)}
          />

          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by location"
            style={{ width: 220 }}
            options={locationOptions}
            value={selectedLocations}
            onChange={setSelectedLocations}
          />

          <Select
            mode="multiple"
            allowClear
            placeholder="Filter by type"
            style={{ width: 220 }}
            options={documentTypeOptions}
            value={selectedDocTypes}
            onChange={setSelectedDocTypes}
          />

          <Segmented
            size="small"
            options={[
              { label: "Compact", value: "small" },
              { label: "Default", value: "middle" },
              { label: "Large", value: "large" },
            ]}
            value={tableSize}
            onChange={(v) => setTableSize(v as TableProps["size"])}
          />

          <Button
            icon={<ReloadOutlined />}
            onClick={() => {
              setSelectedDocTypes([]);
              setSelectedLocations([]);
              setGlobalSearch("");
            }}
          >
            Reset
          </Button>
          <Button
            type="primary"
            onClick={downloadVisibleTableAsCSV}
            disabled={filteredData.length === 0}
          >
            Download
          </Button>
        </Space>
      </Row>

      <Table
        rowKey="id"
        size={tableSize}
        columns={columns}
        dataSource={filteredData}
        pagination={{ pageSize: 10, showSizeChanger: true }}
        sticky
        scroll={{ x: "max-content" }}
      />

      <Modal
        open={isPreviewOpen}
        title="Document Preview"
        onCancel={() => {
          setIsPreviewOpen(false);
          setSignedUrl(null);
        }}
        width="80%"
        footer={[
          <Button
            key="download"
            type="primary"
            disabled={!signedUrl}
            onClick={() => {
              const link = document.createElement("a");
              link.href = signedUrl || "";
              link.download = "";
              link.target = "_blank";
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);
            }}
          >
            Download
          </Button>,
          <Button
            key="close"
            onClick={() => {
              setIsPreviewOpen(false);
              setSignedUrl(null);
            }}
          >
            Close
          </Button>,
        ]}
      >
        {loadingPreview && signedUrl ? (
          <div style={{ textAlign: "center" }}>Loading...</div>
        ) : signedUrl && isPdf(signedUrl) ? (
          <iframe src={signedUrl} width="100%" style={{ height: "75vh" }} />
        ) : (
          <img
            src={signedUrl || ""}
            alt="Document Preview"
            style={{
              maxWidth: "100%",
              maxHeight: "75vh",
              objectFit: "contain",
              display: "block",
              margin: "0 auto",
            }}
          />
        )}
      </Modal>
    </Layout>
  );
};

export default DocumentTableComponent;
